// Google Analytics 4 (gtag.js) client-side analytics — single source of truth.
// No Measurement Protocol / API secret is used; everything runs through window.gtag.

import { GA_MEASUREMENT_ID } from '../config/analytics.config';

let measurementId: string | undefined;
let initialized = false;

const canTrack = (): boolean => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Sets up window.dataLayer and the gtag() stub, then loads gtag.js asynchronously.
 * The stub is defined before the script tag is appended so calls queue immediately.
 * Call once at app startup. No-ops silently if GA_MEASUREMENT_ID is missing.
 */
export const initializeAnalytics = (): void => {
  if (initialized) {
    console.log('[GA4 Debug]', 'initializeAnalytics', { note: 'already initialized, skipping' });
    return;
  }

  measurementId = GA_MEASUREMENT_ID;

  if (!measurementId) {
    console.warn('[GA4 Debug] No measurement ID found — analytics disabled');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    transport_type: 'beacon',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  initialized = true;

  console.log('[GA4 Debug] Analytics initialized with ID:', measurementId);
};

/** Fires a page_view event. Call on every SPA route change. */
export const trackPageView = (path: string, title?: string): void => {
  console.log('[GA4 Debug]', 'trackPageView', { path, title });

  if (!canTrack()) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
};

/** Fires a link_click event. Call before opening/navigating to the link. */
export const trackLinkClick = (linkName: string, linkUrl: string, linkLocation: string, tileName: string): void => {
  console.log('[GA4 Debug]', 'trackLinkClick', { linkName, linkUrl, linkLocation, tileName });

  if (!canTrack()) {
    return;
  }

  window.gtag('event', 'link_click', {
    link_name: linkName,
    link_url: linkUrl,
    link_location: linkLocation,
    tile_name: tileName,
    timestamp: new Date().toISOString(),
  });
};

/** Sets user_id and fires a login event. Call once after a successful sign-in. */
export const trackLogin = (userId: string): void => {
  console.log('[GA4 Debug]', 'trackLogin', { userId });

  if (!canTrack()) {
    return;
  }

  window.gtag('config', measurementId, { user_id: userId });
  window.gtag('event', 'login', { method: 'okta' });
};

/** Fires a logout event. Call before the logout action executes. */
export const trackLogout = (): void => {
  console.log('[GA4 Debug]', 'trackLogout');

  if (!canTrack()) {
    return;
  }

  window.gtag('event', 'logout', {});
};

/** Sets user_id without firing an event. Call on every authenticated page load. */
export const setUser = (userId: string): void => {
  console.log('[GA4 Debug]', 'setUser', { userId });

  if (!canTrack()) {
    return;
  }

  window.gtag('config', measurementId, { user_id: userId });
};

/** Fires a role_filter event when the user selects a role-based filter. */
export const trackRoleFilter = (role: string): void => {
  console.log('[GA4 Debug]', 'trackRoleFilter', { role });

  if (!canTrack()) {
    return;
  }

  window.gtag('event', 'role_filter', {
    role_selected: role,
  });
};

/** Fires a search event when the user submits a search query. */
export const trackSearch = (query: string): void => {
  console.log('[GA4 Debug]', 'trackSearch', { query });

  if (!canTrack()) {
    return;
  }

  window.gtag('event', 'search', {
    search_term: query,
  });
};
